import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import type { Post, ProofOfContribution, Sport, Workout } from '@/types';
import { resolveAvatarUrl } from '@/utils/avatar';
import { mapPostRowToLockerWorkout, mapPostRowToPost } from '@/hooks/useWorkouts';
import { athleteAvatars } from '@/utils/athleteAvatars';
import { priceAt } from '@/utils/pricing';
import { withContribution } from '@/lib/proofOfContribution';
import {
  isPostEnhancementSchemaError,
  markPostEnhancementsUnavailable,
  shouldUsePostEnhancements,
} from '@/lib/postSchemaCompat';

type PostRow = Database['public']['Tables']['posts']['Row'];
type ProfileRow = Database['public']['Tables']['profiles']['Row'];
type ContributionRow = Database['public']['Tables']['proof_of_contributions']['Row'];

interface FeedPostRow extends PostRow {
  profiles: Pick<ProfileRow, 'id' | 'display_name' | 'username' | 'avatar_url' | 'sport' | 'type'> | null;
  proof_of_contributions?:
    | (ContributionRow & {
        proof_of_contribution_artifacts?: Database['public']['Tables']['proof_of_contribution_artifacts']['Row'][] | null;
      })
    | Array<
        ContributionRow & {
          proof_of_contribution_artifacts?: Database['public']['Tables']['proof_of_contribution_artifacts']['Row'][] | null;
        }
      >
    | null;
}

export interface ProofOfSweatFeedItem {
  post: Post;
  workout: Workout | null;
  contribution: ProofOfContribution | null;
  athlete: {
    id: string;
    name: string;
    slug: string;
    avatar?: string | null;
    sport?: Sport;
    profileType?: 'human' | 'agent';
    marketCap?: number;
    holdersCount?: number;
  };
}

interface FeedPage {
  items: ProofOfSweatFeedItem[];
  nextPage?: number;
}

interface UseProofOfSweatFeedOptions {
  athleteId?: string;
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 12;

const ensureWorkoutFromPost = (post: Post): Workout => ({
  id: post.id,
  date: post.created_at,
  type: 'Other',
  duration: 0,
  rpe: 5,
  notes: post.text ?? '',
  mediaUrl: typeof post.workout_json === 'object' && post.workout_json !== null && 'mediaUrl' in post.workout_json
    ? (post.workout_json as Record<string, unknown>).mediaUrl as string | undefined
    : post.image_url ?? undefined,
  mediaType: post.image_url ? 'image' : undefined,
  visibility: post.visibility,
  minTokensRequired: post.min_tokens_required ?? 0,
});

export function useProofOfSweatFeed(options: UseProofOfSweatFeedOptions = {}) {
  const { athleteId, pageSize = DEFAULT_PAGE_SIZE } = options;

  return useInfiniteQuery<FeedPage>({
    queryKey: ['proof-of-sweat-feed', athleteId, pageSize],
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    staleTime: 30000, // Consider data fresh for 30 seconds
    queryFn: async ({ pageParam = 0 }) => {
      const currentPage = Number(pageParam) || 0;
      const from = currentPage * pageSize;
      const to = from + pageSize - 1;

      const buildQuery = (includeEnhancements: boolean) =>
        supabase
          .from('posts')
          .select(
            includeEnhancements
              ? `
                  id,
                  created_at,
                  author_id,
                  workout_json,
                  image_url,
                  text,
                  token_gated,
                  strava_activity_id,
                  visibility,
                  min_tokens_required,
                  is_pinned,
                  post_type,
                  monad_tx_hash,
                  location_city,
                  location_country,
                  location_country_code,
                  location_lat,
                  location_lng,
                  profiles:profiles (
                    id,
                    display_name,
                    username,
                    avatar_url,
                    sport,
                    type
                  ),
                  proof_of_contributions (
                    post_id,
                    title,
                    contribution_type,
                    task_brief,
                    workflow_summary,
                    result_summary,
                    started_at,
                    completed_at,
                    duration_minutes,
                    status,
                    verification_status,
                    accepted_by_user_id,
                    accepted_at,
                    verifier_note,
                    task_id,
                    bounty_id,
                    attestation_hash,
                    external_reference,
                    reproducibility_metadata,
                    created_at,
                    updated_at,
                    proof_of_contribution_artifacts (
                      id,
                      contribution_post_id,
                      artifact_type,
                      label,
                      url,
                      storage_path,
                      notes,
                      metadata,
                      sort_order,
                      created_at,
                      updated_at
                    )
                  )
                `
              : `
                  id,
                  created_at,
                  author_id,
                  workout_json,
                  image_url,
                  text,
                  token_gated,
                  strava_activity_id,
                  visibility,
                  min_tokens_required,
                  is_pinned,
                  profiles:profiles (
                    id,
                    display_name,
                    username,
                    avatar_url,
                    sport,
                    type
                  )
                `,
            { count: 'exact' },
          )
          .order('created_at', { ascending: false })
          .range(from, to);

      const preferEnhancements = shouldUsePostEnhancements();
      let query = buildQuery(preferEnhancements);

      if (athleteId) {
        query = query.eq('author_id', athleteId);
      }

      let { data, error, count } = await query;

      if (preferEnhancements && error && isPostEnhancementSchemaError(error)) {
        markPostEnhancementsUnavailable();
        query = buildQuery(false);
        if (athleteId) {
          query = query.eq('author_id', athleteId);
        }
        ({ data, error, count } = await query);
      }

      if (error) {
        throw error;
      }

      const rows = (data ?? []) as unknown as FeedPostRow[];

      // Batch fetch athlete tokens for market cap calculation
      const authorIds = rows.map(row => row.author_id).filter(Boolean);
      const uniqueAuthorIds = [...new Set(authorIds)];

      const tokensMap = new Map<string, { supply: number; a: number; b: number; c: number }>();
      if (uniqueAuthorIds.length > 0) {
        const { data: tokensData } = await supabase
          .from('athlete_tokens')
          .select('athlete_id, supply, a, b, c')
          .in('athlete_id', uniqueAuthorIds);

        if (tokensData) {
          tokensData.forEach((token) => {
            tokensMap.set(token.athlete_id, {
              supply: token.supply,
              a: token.a,
              b: token.b,
              c: token.c,
            });
          });
        }
      }

      // Batch fetch holder counts
      const holdersMap = new Map<string, number>();
      if (uniqueAuthorIds.length > 0) {
        const { data: holdingsData } = await supabase
          .from('holdings')
          .select('athlete_id, user_id')
          .in('athlete_id', uniqueAuthorIds)
          .gt('qty', 0);

        if (holdingsData) {
          // Count unique holders per athlete
          const holdersByAthlete = holdingsData.reduce((acc, holding) => {
            if (!acc[holding.athlete_id]) {
              acc[holding.athlete_id] = new Set<string>();
            }
            acc[holding.athlete_id].add(holding.user_id);
            return acc;
          }, {} as Record<string, Set<string>>);

          Object.entries(holdersByAthlete).forEach(([athleteId, userIds]) => {
            holdersMap.set(athleteId, userIds.size);
          });
        }

        // For any athletes with tokens but no holdings data, set holders to 0
        uniqueAuthorIds.forEach(athleteId => {
          if (!holdersMap.has(athleteId)) {
            holdersMap.set(athleteId, 0);
          }
        });
      }

      const items: ProofOfSweatFeedItem[] = rows.map((row) => {
        const lockerWorkout = mapPostRowToLockerWorkout(row);
        const post = withContribution(mapPostRowToPost(row), row.proof_of_contributions);
        const workout = post.post_type === 'proof_of_sweat'
          ? (lockerWorkout.workout ?? ensureWorkoutFromPost(post))
          : null;

        const profile = row.profiles;
        const slug = profile?.username ?? row.author_id;
        const name = profile?.display_name || profile?.username || 'Unknown Athlete';

        // Calculate market cap from bonding curve
        let marketCap: number | undefined;
        const tokenData = tokensMap.get(row.author_id);
        if (tokenData && tokenData.supply > 0) {
          const price = priceAt(tokenData.supply, {
            a: tokenData.a,
            b: tokenData.b,
            c: tokenData.c,
          });
          marketCap = price * tokenData.supply;
        }

        // Get holder count
        const holdersCount = holdersMap.get(row.author_id);

        return {
          post,
          workout: workout
            ? {
                ...workout,
                mediaUrl: workout.mediaUrl ?? lockerWorkout.imageUrl ?? post.image_url ?? undefined,
                visibility: post.visibility,
                minTokensRequired: post.min_tokens_required ?? workout.minTokensRequired,
              }
            : null,
          contribution: post.proof_of_contribution ?? null,
          athlete: {
            id: row.author_id,
            name,
            slug,
            sport: (profile?.sport as Sport) || 'Running',
            profileType: (profile?.type as 'human' | 'agent' | undefined) ?? 'human',
            avatar: resolveAvatarUrl(
              (profile?.username && athleteAvatars[profile.username]) ?? profile?.avatar_url ?? undefined,
              {
                size: 160,
                seed: profile?.username ?? row.author_id,
              }
            ),
            marketCap,
            holdersCount,
          },
        };
      });

      const total = count ?? rows.length;
      const hasMore = to + 1 < total;

      return {
        items,
        nextPage: hasMore ? currentPage + 1 : undefined,
      };
    },
  });
}
